/**
 * AlertNotificationService - Multi-channel alert orchestrator with user preferences and retry logic
 *
 * Boundary: Notification routing and delivery; does NOT generate alert content or manage templates.
 * External dependencies:
 * - Azure Functions: /api/email, /api/sms, /api/signalr (via AlertServiceConfig)
 * - NotificationPreferencesService: User channel preferences
 * - EmailTemplateService: Pre-rendered email HTML
 * - NotificationManagerService, ToastNotificationService: In-app notifications
 * - AlertNotificationBridgeService: Cross-component alert state
 */
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import { LoggingService } from './logging.service';
import { ToastNotificationService } from './toast-notification.service';
import { NotificationManagerService } from './notification-manager.service';
import { NotificationPreferencesService } from './notification-preferences.service';
import { NotificationType } from '@core/models/notification.types';
import { EmailTemplateService } from './email-template-embedded.service';
import { AlertNotificationBridgeService } from './alert-notification-bridge.service';
import {
  CriticalVulnerabilityTemplateData,
  SecurityAlertTemplateData,
  ScanCompletionTemplateData,
  SystemMaintenanceTemplateData,
} from './email-template-embedded.service';
import { ApiConfigurationService } from './api-configuration.service';
import { Auth } from '@angular/fire/auth';
import { environment } from '../../../environments/environment';

// Alert severity levels based on the examples
export enum AlertSeverity {
  CRITICAL = 'P1', // Service down, data breach, critical vulnerabilities
  HIGH = 'P2', // Major functionality impacted, high vulnerabilities
  MEDIUM = 'P3', // Minor functionality impacted, medium vulnerabilities
  LOW = 'P4', // Enhancement requests, low vulnerabilities
  INFO = 'INFO', // Informational updates
}

// Alert types for different scenarios
export enum AlertType {
  VULNERABILITY_SCAN = 'vulnerability_scan',
  COMPLIANCE_DRIFT = 'compliance_drift',
  INCIDENT_RESPONSE = 'incident_response',
  TICKET_UPDATE = 'ticket_update',
  SCAN_COMPLETION = 'scan_completion',
  SYSTEM_STATUS = 'system_status',
  WEEKLY_REPORT = 'weekly_report',
}

// Notification channels based on Azure Function endpoints
export enum NotificationChannel {
  EMAIL = 'email',
  SMS = 'sms',
  SIGNALR = 'signalr',
  DESKTOP = 'desktop',
  IN_APP = 'in_app',
  WEBHOOK = 'webhook',
}

// User notification preferences
export interface UserNotificationPreferences {
  userId: string;
  email?: string;
  phone?: string;

  // Channel preferences by alert type and severity
  preferences: {
    [alertType in AlertType]?: {
      [severity in AlertSeverity]?: NotificationChannel[];
    };
  };

  // Global settings
  enableDesktopNotifications: boolean;
  enableInAppNotifications: boolean;
  enableEmailNotifications: boolean;
  enableSmsNotifications: boolean;
  quietHoursStart?: string; // HH:MM format
  quietHoursEnd?: string; // HH:MM format
  timezone?: string;
}

// Alert payload structure
export interface AlertPayload {
  id?: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  timestamp: Date;

  // Context-specific data
  metadata?: {
    scanId?: string;
    ticketId?: string;
    currentCompanyId?: string;
    userId?: string;
    vulnerabilityData?: any;
    complianceData?: any;
    incidentData?: any;
    [key: string]: any;
  };

  // Action buttons for notifications
  actions?: {
    label: string;
    url?: string;
    callback?: () => void;
  }[];

  // Override default notification preferences
  forceChannels?: NotificationChannel[];
  recipients?: string[]; // Specific recipients for this alert
}

// Azure Function response structure
export interface AlertResponse {
  success: boolean;
  channel: NotificationChannel;
  messageId?: string;
  error?: string;
  retryAfter?: number;
}

// Configuration for Azure Functions
export interface AlertServiceConfig {
  functionUrl: string;
  functionKey: string;
  defaultRetries: number;
  retryDelayMs: number;
  timeoutMs: number;
}

/**
 * Orchestrates multi-channel alert delivery (Email, SMS, SignalR, Desktop, In-App, Webhook)
 * based on user preferences, alert type, and severity. Implements retry logic with
 * exponential backoff and quiet hours support.
 *
 * Dependencies:
 * - HttpClient: POST to Azure Functions endpoints
 * - LoggingService: Error logging
 * - ToastNotificationService: In-app toast notifications
 * - NotificationManagerService: Desktop/browser notifications
 * - NotificationPreferencesService: User channel preferences
 * - EmailTemplateService: Pre-rendered email HTML templates
 * - AlertNotificationBridgeService: Cross-component alert state sync
 *
 * Side effects:
 * - BehaviorSubject: userPreferences$ (user notification settings)
 * - Array: alertQueue (queued alerts for batching)
 * - Property: isProcessingQueue (mutex for queue processing)
 * - HTTP: POST to Azure Functions (email, SMS, SignalR endpoints)
 * - Local notifications: Desktop push notifications via NotificationManagerService
 *
 * Notification flow:
 * 1. Alert submitted via sendAlert()
 * 2. User preferences fetched/merged with default preferences
 * 3. Channels determined by alert type + severity + user overrides
 * 4. Quiet hours check (skip or queue if in quiet period)
 * 5. Parallel channel delivery with retry logic
 * 6. Results logged and errors surfaced
 */
@Injectable({
  providedIn: 'root',
})
export class AlertNotificationService {
  private http = inject(HttpClient);
  private loggingService = inject(LoggingService);
  private toastService = inject(ToastNotificationService);
  private notificationManager = inject(NotificationManagerService);
  private notificationPreferencesService = inject(
    NotificationPreferencesService,
  );
  private emailTemplateService = inject(EmailTemplateService);
  private alertBridgeService = inject(AlertNotificationBridgeService);
  private apiConfigService = inject(ApiConfigurationService);
  private platformId = inject(PLATFORM_ID);
  // Optional so unit tests without Firebase providers still construct the service.
  private auth = inject(Auth, { optional: true });

  private readonly config: AlertServiceConfig;
  private userPreferences$ =
    new BehaviorSubject<UserNotificationPreferences | null>(null);
  private alertQueue: AlertPayload[] = [];
  private isProcessingQueue = false;

  // Default notification preferences
  private readonly defaultPreferences: Partial<UserNotificationPreferences> = {
    enableDesktopNotifications: true,
    enableInAppNotifications: true,
    enableEmailNotifications: true,
    enableSmsNotifications: false,
    preferences: {
      [AlertType.VULNERABILITY_SCAN]: {
        [AlertSeverity.CRITICAL]: [
          NotificationChannel.EMAIL,
          NotificationChannel.SMS,
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.HIGH]: [
          NotificationChannel.EMAIL,
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.MEDIUM]: [
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
        [AlertSeverity.INFO]: [NotificationChannel.IN_APP],
      },
      [AlertType.SCAN_COMPLETION]: {
        [AlertSeverity.INFO]: [
          NotificationChannel.DESKTOP,
          NotificationChannel.IN_APP,
        ],
      },
      [AlertType.TICKET_UPDATE]: {
        [AlertSeverity.CRITICAL]: [
          NotificationChannel.EMAIL,
          NotificationChannel.SMS,
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.HIGH]: [
          NotificationChannel.EMAIL,
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.MEDIUM]: [
          NotificationChannel.EMAIL,
          NotificationChannel.DESKTOP,
          NotificationChannel.IN_APP,
        ],
        [AlertSeverity.LOW]: [
          NotificationChannel.EMAIL,
          NotificationChannel.IN_APP,
        ],
        [AlertSeverity.INFO]: [
          NotificationChannel.EMAIL,
          NotificationChannel.IN_APP,
        ],
      },
      [AlertType.COMPLIANCE_DRIFT]: {
        [AlertSeverity.CRITICAL]: [
          NotificationChannel.EMAIL,
          NotificationChannel.SMS,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.HIGH]: [
          NotificationChannel.EMAIL,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.MEDIUM]: [
          NotificationChannel.EMAIL,
          NotificationChannel.IN_APP,
        ],
        [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
      },
      [AlertType.INCIDENT_RESPONSE]: {
        [AlertSeverity.CRITICAL]: [
          NotificationChannel.EMAIL,
          NotificationChannel.SMS,
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.HIGH]: [
          NotificationChannel.EMAIL,
          NotificationChannel.DESKTOP,
          NotificationChannel.SIGNALR,
        ],
        [AlertSeverity.MEDIUM]: [
          NotificationChannel.EMAIL,
          NotificationChannel.DESKTOP,
        ],
        [AlertSeverity.LOW]: [
          NotificationChannel.EMAIL,
          NotificationChannel.IN_APP,
        ],
      },
    },
  };

  constructor() {
    // Initialize configuration from environment and API config service
    this.config = {
      functionUrl: environment.azureFunctions?.alertsUrl || '',
      functionKey: '', // Will be populated from ApiConfigurationService
      defaultRetries: 3,
      retryDelayMs: 1000,
      timeoutMs: 30000,
    };

    // Populate function key from ApiConfigurationService
    this.apiConfigService.getAlertsFunctionToken()
      .then(token => {
        this.config.functionKey = token.value;
      })
      .catch(error => {
        this.loggingService.logError('Failed to get alerts function token', error);
      });

    // SECURITY: Validate configuration without exposing secrets
    if (!this.config.functionUrl) {
      if (environment.production) {
        this.loggingService.logError(
          '⚠️ Alert service configuration incomplete - Azure Functions not configured',
        );
      } else {
        this.loggingService.logDebug(
          'ℹ️ Alert service running in development mode - email/SMS notifications disabled, desktop/in-app notifications available',
        );
      }
    } else {
      this.loggingService.logDebug(
        '✅ Alert service configured with Azure Functions support',
      );
    }
  }

  /**
   * Initialize the service with user preferences
   */
  async initialize(userId: string): Promise<boolean> {
    try {
      const preferences = await this.loadUserPreferences(userId);
      this.userPreferences$.next(preferences);
      return true;
    } catch (error) {
      this.loggingService.logError('❌ Failed to initialize alert service:', {
        error,
        userId,
      });
      // Use default preferences as fallback
      this.userPreferences$.next({
        userId,
        ...this.defaultPreferences,
      } as UserNotificationPreferences);
      return false;
    }
  }

  /**
   * Initialize the service with user preferences and role
   */
  async initializeWithRole(
    userId: string,
    userRole: string = 'user',
  ): Promise<boolean> {
    try {
      const preferences = await this.loadUserPreferencesWithRole(
        userId,
        userRole,
      );
      this.userPreferences$.next(preferences);
      return true;
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to initialize alert service with role:',
        { error, userId, userRole },
      );
      // Use default preferences as fallback
      this.userPreferences$.next({
        userId,
        ...this.defaultPreferences,
      } as UserNotificationPreferences);
      return false;
    }
  }

  /**
   * Send a critical vulnerability alert
   */
  async sendCriticalVulnerabilityAlert(
    vulnerabilityData: any,
  ): Promise<AlertResponse[]> {
    // Generate scan details URL if scan template ID is available
    const scanTemplateId =
      vulnerabilityData.scanTemplateId || vulnerabilityData.template_id;
    const actions = [
      {
        label: 'View Vulnerability',
        url: `/vulnerabilities/${vulnerabilityData.cve_id}`,
      },
    ];

    // Add scan details action if template ID is available
    if (scanTemplateId) {
      actions.push({
        label: 'View Scan Details',
        url: `/dashboard/scans/view/${scanTemplateId}`,
      });
    }

    const alert: AlertPayload = {
      type: AlertType.VULNERABILITY_SCAN,
      severity: AlertSeverity.CRITICAL,
      title: `CRITICAL: ${vulnerabilityData.cve_id} detected`,
      message: this.formatVulnerabilityMessage(vulnerabilityData),
      timestamp: new Date(),
      metadata: {
        vulnerabilityData,
        scanId: vulnerabilityData.scanId,
        currentCompanyId: vulnerabilityData.currentCompanyId,
        scanTemplateId: scanTemplateId,
      },
      actions: actions,
    };

    return this.sendAlert(alert);
  }

  /**
   * Send scan completion notification
   */
  async sendScanCompletionAlert(scanData: any): Promise<AlertResponse[]> {
    const severity = this.determineScanSeverity(scanData);

    // Generate scan details URL using scan template ID
    const scanTemplateId =
      scanData.templateId || scanData.template_id || scanData.id;
    const scanDetailsUrl = `/dashboard/scans/view/${scanTemplateId}`;

    const alert: AlertPayload = {
      type: AlertType.SCAN_COMPLETION,
      severity,
      title: `Scan Completed: ${scanData.name}`,
      message: this.formatScanCompletionMessage(scanData),
      timestamp: new Date(),
      metadata: {
        scanId: scanData.id,
        currentCompanyId: scanData.currentCompanyId,
        scanTemplateId: scanTemplateId,
      },
      actions: [
        {
          label: 'View Scan Details',
          url: scanDetailsUrl,
        },
      ],
    };

    return this.sendAlert(alert);
  }

  /**
   * Send ticket update notification
   */
  async sendTicketUpdateAlert(
    ticketData: any,
    updateType: string,
  ): Promise<AlertResponse[]> {
    const severity = this.determineTicketSeverity(ticketData, updateType);

    // Generate ticket URL using the dashboard support ticket format
    const ticketUrl = `/dashboard/support/ticket/${ticketData.id}`;

    const alert: AlertPayload = {
      type: AlertType.TICKET_UPDATE,
      severity,
      title: this.formatTicketTitle(ticketData, updateType),
      message: this.formatTicketMessage(ticketData, updateType),
      timestamp: new Date(),
      metadata: {
        ticketId: ticketData.id,
        updateType,
        currentCompanyId: ticketData.currentCompanyId,
      },
      actions: [
        {
          label: 'View Ticket',
          url: ticketUrl,
        },
      ],
    };

    return this.sendAlert(alert);
  }

  /**
   * Send compliance drift alert
   */
  async sendComplianceDriftAlert(
    complianceData: any,
  ): Promise<AlertResponse[]> {
    const alert: AlertPayload = {
      type: AlertType.COMPLIANCE_DRIFT,
      severity: this.mapStringToSeverity(complianceData.severity),
      title: `Compliance Drift: ${complianceData.control_name}`,
      message: this.formatComplianceMessage(complianceData),
      timestamp: new Date(),
      metadata: {
        complianceData,
        framework: complianceData.framework,
      },
      actions: [
        {
          label: 'Review Compliance',
          url: `/compliance/${complianceData.framework}`,
        },
      ],
    };

    return this.sendAlert(alert);
  }

  /**
   * Send incident response alert
   */
  async sendIncidentResponseAlert(incidentData: any): Promise<AlertResponse[]> {
    const alert: AlertPayload = {
      type: AlertType.INCIDENT_RESPONSE,
      severity: this.mapStringToSeverity(incidentData.severity),
      title: `${incidentData.severity}: ${incidentData.title}`,
      message: this.formatIncidentMessage(incidentData),
      timestamp: new Date(),
      metadata: {
        incidentData,
        incidentId: incidentData.id,
      },
      actions: [
        {
          label: 'Join Bridge',
          url: incidentData.bridge_url,
        },
        {
          label: 'View Incident',
          url: `/incidents/${incidentData.id}`,
        },
      ],
    };

    return this.sendAlert(alert);
  }

  /**
   * Main alert sending method
   */
  private async sendAlert(alert: AlertPayload): Promise<AlertResponse[]> {
    try {
      // Generate unique ID for tracking
      alert.id = this.generateAlertId();
      // Determine notification channels based on preferences
      const channels = this.determineNotificationChannels(alert);

      if (channels.length === 0) {
        this.loggingService.logWarning(
          '⚠️ No notification channels configured for alert',
          { alert },
        );
        return [];
      }

      // Check quiet hours
      if (
        this.isInQuietHours() &&
        !this.shouldOverrideQuietHours(alert.severity)
      ) {
        this.alertQueue.push(alert);
        return [];
      }

      // Send notifications concurrently
      const responses = await this.sendMultiChannelNotification(
        alert,
        channels,
      );

      // Add alert to bridge service for header notifications
      this.alertBridgeService.addAlert({
        type: alert.type,
        severity: alert.severity,
        title: alert.title,
        message: alert.message,
        channels: channels.map((c) => c.toString()),
        metadata: {
          ...alert.metadata,
          alertId: alert.id,
          timestamp: alert.timestamp,
          actions: alert.actions,
          source: 'alert-notification-service',
        },
      });
      return responses;
    } catch (error) {
      this.loggingService.logError('❌ Failed to send alert:', error);
      throw error;
    }
  }

  /**
   * Send notifications across multiple channels
   */
  private async sendMultiChannelNotification(
    alert: AlertPayload,
    channels: NotificationChannel[],
  ): Promise<AlertResponse[]> {
    const notifications = channels.map(async (channel) => {
      try {
        switch (channel) {
          case NotificationChannel.EMAIL:
            return await this.sendEmailNotification(alert);
          case NotificationChannel.SMS:
            return await this.sendSmsNotification(alert);
          case NotificationChannel.SIGNALR:
            return await this.sendSignalRNotification(alert);
          case NotificationChannel.DESKTOP:
            return await this.sendDesktopNotification(alert);
          case NotificationChannel.IN_APP:
            return await this.sendInAppNotification(alert);
          case NotificationChannel.WEBHOOK:
            return await this.sendWebhookNotification(alert);
          default:
            throw new Error(`Unsupported notification channel: ${channel}`);
        }
      } catch (error) {
        this.loggingService.logError(
          `❌ Failed to send ${channel} notification:`,
          {
            channel,
            alertId: alert.id,
            errorType:
              error instanceof Error ? error.constructor.name : 'UnknownError',
            // SECURITY: Don't log full error details in production
            errorMessage: environment.production
              ? 'Notification failed'
              : error instanceof Error
                ? error.message
                : 'Unknown error',
          },
        );
        return {
          success: false,
          channel,
          error:
            error instanceof Error
              ? 'Notification delivery failed'
              : 'Unknown error',
        };
      }
    });

    return Promise.all(notifications);
  }

  /**
   * Send email notification via Azure Function
   */
  private async sendEmailNotification(
    alert: AlertPayload,
  ): Promise<AlertResponse> {
    const preferences = this.userPreferences$.value;

    if (!preferences?.email || !preferences.enableEmailNotifications) {
      return {
        success: false,
        channel: NotificationChannel.EMAIL,
        error: 'Email not configured',
      };
    }

    // Generate email content using templates
    const emailContent = this.generateEmailContent(alert);

    const payload = {
      to: preferences.email,
      subject: emailContent.subject,
      body: emailContent.htmlContent,
      isHtml: true,
    };

    return this.callAzureFunction(
      'send_email_alert',
      payload,
      NotificationChannel.EMAIL,
    );
  }

  /**
   * Generate email content including subject and body using templates
   */
  private generateEmailContent(alert: AlertPayload): {
    subject: string;
    htmlContent: string;
  } {
    try {
      // Generate template data based on alert type
      const templateData = this.generateTemplateData(alert);

      // Get the appropriate template based on alert type
      let template;

      switch (alert.type) {
        case AlertType.VULNERABILITY_SCAN:
          if (
            alert.severity === AlertSeverity.CRITICAL &&
            alert.metadata?.vulnerabilityData
          ) {
            template =
              this.emailTemplateService.getCriticalVulnerabilityTemplate(
                templateData as CriticalVulnerabilityTemplateData,
              );
          } else {
            template = this.emailTemplateService.getSecurityAlertTemplate(
              templateData as SecurityAlertTemplateData,
            );
          }
          break;

        case AlertType.SCAN_COMPLETION:
          template = this.emailTemplateService.getScanCompletionTemplate(
            templateData as ScanCompletionTemplateData,
          );
          break;

        case AlertType.TICKET_UPDATE:
        case AlertType.COMPLIANCE_DRIFT:
        case AlertType.INCIDENT_RESPONSE:
        case AlertType.SYSTEM_STATUS:
          template = this.emailTemplateService.getSecurityAlertTemplate(
            templateData as SecurityAlertTemplateData,
          );
          break;

        case AlertType.WEEKLY_REPORT:
          template = this.emailTemplateService.getSystemMaintenanceTemplate(
            templateData as SystemMaintenanceTemplateData,
          );
          break;

        default:
          // Fallback to security alert template for unknown types
          template = this.emailTemplateService.getSecurityAlertTemplate(
            templateData as SecurityAlertTemplateData,
          );
          break;
      }

      return {
        subject: template.subject,
        htmlContent: template.htmlContent,
      };
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to generate email template, using fallback:',
        error,
      );

      // Fallback to simple email template if template generation fails
      return {
        subject: alert.title,
        htmlContent: this.generateFallbackEmailBody(alert),
      };
    }
  }

  /**
   * Send SMS notification via Azure Function
   */
  private async sendSmsNotification(
    alert: AlertPayload,
  ): Promise<AlertResponse> {
    const preferences = this.userPreferences$.value;

    if (!preferences?.phone || !preferences.enableSmsNotifications) {
      return {
        success: false,
        channel: NotificationChannel.SMS,
        error: 'SMS not configured',
      };
    }

    const payload = {
      to: preferences.phone,
      message: this.formatSmsMessage(alert),
    };

    return this.callAzureFunction(
      'send_sms_alert',
      payload,
      NotificationChannel.SMS,
    );
  }

  /**
   * Send SignalR notification via Azure Function
   */
  private async sendSignalRNotification(
    alert: AlertPayload,
  ): Promise<AlertResponse> {
    const preferences = this.userPreferences$.value;

    if (!preferences?.userId) {
      return {
        success: false,
        channel: NotificationChannel.SIGNALR,
        error: 'User ID not available',
      };
    }

    const payload = {
      userId: preferences.userId,
      target: this.getSignalRTarget(alert.type),
      data: {
        id: alert.id,
        type: alert.type,
        severity: alert.severity,
        title: alert.title,
        message: alert.message,
        timestamp: alert.timestamp.toISOString(),
        metadata: alert.metadata,
        actions: alert.actions,
      },
    };

    return this.callAzureFunction(
      'push_signalr_message',
      payload,
      NotificationChannel.SIGNALR,
    );
  }

  /**
   * Send desktop notification using local browser API
   */
  private async sendDesktopNotification(
    alert: AlertPayload,
  ): Promise<AlertResponse> {
    const preferences = this.userPreferences$.value;

    if (!preferences?.enableDesktopNotifications) {
      return {
        success: false,
        channel: NotificationChannel.DESKTOP,
        error: 'Desktop notifications disabled',
      };
    }

    try {
      const action = alert.actions?.[0];

      const notificationId = this.toastService.showCustomDesktopNotification({
        title: this.getSeverityEmoji(alert.severity) + ' ' + alert.title,
        body: alert.message,
        icon: '/assets/images/logo/scan-ninja-icon.png',
        tag: `alert-${alert.id}`,
        requireInteraction: alert.severity === AlertSeverity.CRITICAL,
        data: {
          alertId: alert.id,
          actionUrl: action?.url,
        },
      });

      return {
        success: !!notificationId,
        channel: NotificationChannel.DESKTOP,
        messageId: alert.id,
      };
    } catch (error) {
      return {
        success: false,
        channel: NotificationChannel.DESKTOP,
        error:
          error instanceof Error
            ? error.message
            : 'Desktop notification failed',
      };
    }
  }

  /**
   * Send in-app notification using toast service
   */
  private async sendInAppNotification(
    alert: AlertPayload,
  ): Promise<AlertResponse> {
    const preferences = this.userPreferences$.value;

    if (!preferences?.enableInAppNotifications) {
      return {
        success: false,
        channel: NotificationChannel.IN_APP,
        error: 'In-app notifications disabled',
      };
    }

    try {
      const action = alert.actions?.[0];
      const toastAction = action
        ? {
            label: action.label,
            callback:
              action.callback ||
              (() => {
                if (action.url && isPlatformBrowser(this.platformId)) {
                  window.open(action.url, '_blank');
                }
              }),
          }
        : undefined;

      // Map AlertType to NotificationType for notification manager
      const notificationType = this.mapAlertTypeToNotificationType(alert.type);

      let notificationId: string | null;

      switch (alert.severity) {
        case AlertSeverity.CRITICAL:
          notificationId = this.notificationManager.showError(
            alert.title,
            alert.message,
            0,
            toastAction,
            notificationType,
          );
          break;
        case AlertSeverity.HIGH:
          notificationId = this.notificationManager.showWarning(
            alert.title,
            alert.message,
            10000,
            toastAction,
            notificationType,
          );
          break;
        case AlertSeverity.MEDIUM:
          notificationId = this.notificationManager.showInfo(
            alert.title,
            alert.message,
            8000,
            toastAction,
            notificationType,
          );
          break;
        case AlertSeverity.LOW:
        case AlertSeverity.INFO:
        default:
          notificationId = this.notificationManager.showSuccess(
            alert.title,
            alert.message,
            5000,
            toastAction,
            notificationType,
          );
          break;
      }

      return {
        success: true,
        channel: NotificationChannel.IN_APP,
        messageId: notificationId || 'notification-suppressed',
      };
    } catch (error) {
      return {
        success: false,
        channel: NotificationChannel.IN_APP,
        error:
          error instanceof Error ? error.message : 'In-app notification failed',
      };
    }
  }

  /**
   * Send webhook notification via Azure Function
   */
  private async sendWebhookNotification(
    _alert: AlertPayload,
  ): Promise<AlertResponse> {
    // Implementation for webhook notifications if needed
    return {
      success: false,
      channel: NotificationChannel.WEBHOOK,
      error: 'Webhook not implemented',
    };
  }

  /**
   * Call Azure Function with retry logic
   */
  private async callAzureFunction(
    endpoint: string,
    payload: any,
    channel: NotificationChannel,
  ): Promise<AlertResponse> {
    // Check if Azure Functions are configured
    if (!this.config.functionUrl || !this.config.functionKey) {
      this.loggingService.logDebug(
        `🔧 ${endpoint} skipped - Azure Functions not configured for development`,
      );
      return {
        success: false,
        channel,
        error: 'Azure Functions not configured for development environment',
      };
    }

    const url = `${this.config.functionUrl}/${endpoint}`;
    // The alerts app binds each send to the signed-in user (it only emails,
    // texts or pushes to the caller), so it needs the Firebase ID token; the
    // function key alone is rejected with 401.
    const idToken = await this.auth?.currentUser
      ?.getIdToken()
      .catch(() => null);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-functions-key': this.config.functionKey,
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    });

    const request = this.http.post(url, payload, { headers }).pipe(
      retry({
        count: this.config.defaultRetries,
        delay: (error, retryCount) => {
          const delay = this.config.retryDelayMs * Math.pow(2, retryCount - 1);
          this.loggingService.logWarning(
            `🔄 Retrying ${endpoint} (attempt ${retryCount}) after ${delay}ms`,
          );
          return new Promise((resolve) => setTimeout(resolve, delay));
        },
      }),
      map((response: any) => ({
        success: true,
        channel,
        messageId: response.messageId || response.id,
      })),
      catchError((error) => {
        this.loggingService.logError(
          `❌ Azure Function ${endpoint} failed:`,
          error,
        );
        return [
          {
            success: false,
            channel,
            error:
              error.error?.message ||
              error.message ||
              'Azure Function call failed',
          },
        ];
      }),
    );

    return firstValueFrom(request) as Promise<AlertResponse>;
  }

  /**
   * Check if Azure Functions are properly configured
   */
  isAzureFunctionsConfigured(): boolean {
    return !!(this.config.functionUrl && this.config.functionKey);
  }

  /**
   * Get current service configuration status
   */
  getServiceStatus(): {
    azureFunctionsEnabled: boolean;
    localNotificationsEnabled: boolean;
  } {
    return {
      azureFunctionsEnabled: this.isAzureFunctionsConfigured(),
      localNotificationsEnabled: true, // Desktop and in-app notifications are always available
    };
  }

  /**
   * Determine notification channels based on user preferences and alert properties
   */
  private determineNotificationChannels(
    alert: AlertPayload,
  ): NotificationChannel[] {
    const preferences = this.userPreferences$.value;

    if (!preferences) {
      return [NotificationChannel.IN_APP]; // Fallback
    }

    // Use forced channels if specified
    if (alert.forceChannels && alert.forceChannels.length > 0) {
      return alert.forceChannels;
    }

    // Get channels from user preferences
    const typePreferences = preferences.preferences[alert.type];
    if (typePreferences && typePreferences[alert.severity]) {
      const severityPreferences = typePreferences[alert.severity];
      if (severityPreferences) {
        return severityPreferences.filter((channel) =>
          this.isChannelEnabled(channel, preferences),
        );
      }
    }

    // Fallback to default channels based on severity
    switch (alert.severity) {
      case AlertSeverity.CRITICAL:
        return [
          NotificationChannel.EMAIL,
          NotificationChannel.DESKTOP,
          NotificationChannel.IN_APP,
        ];
      case AlertSeverity.HIGH:
        return [NotificationChannel.DESKTOP, NotificationChannel.IN_APP];
      case AlertSeverity.MEDIUM:
        return [NotificationChannel.IN_APP];
      case AlertSeverity.LOW:
      case AlertSeverity.INFO:
      default:
        return [NotificationChannel.IN_APP];
    }
  }

  /**
   * Check if a notification channel is enabled for the user
   */
  private isChannelEnabled(
    channel: NotificationChannel,
    preferences: UserNotificationPreferences,
  ): boolean {
    switch (channel) {
      case NotificationChannel.EMAIL:
        return preferences.enableEmailNotifications && !!preferences.email;
      case NotificationChannel.SMS:
        return preferences.enableSmsNotifications && !!preferences.phone;
      case NotificationChannel.DESKTOP:
        return preferences.enableDesktopNotifications;
      case NotificationChannel.IN_APP:
        return preferences.enableInAppNotifications;
      case NotificationChannel.SIGNALR:
      case NotificationChannel.WEBHOOK:
        return true; // Always available if configured
      default:
        return false;
    }
  }

  /**
   * Check if current time is within quiet hours
   */
  private isInQuietHours(): boolean {
    const preferences = this.userPreferences$.value;

    if (!preferences?.quietHoursStart || !preferences?.quietHoursEnd) {
      return false;
    }

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = preferences.quietHoursStart
      .split(':')
      .map(Number);
    const [endHour, endMin] = preferences.quietHoursEnd.split(':').map(Number);

    const quietStart = startHour * 60 + startMin;
    const quietEnd = endHour * 60 + endMin;

    if (quietStart <= quietEnd) {
      return currentTime >= quietStart && currentTime <= quietEnd;
    } else {
      // Quiet hours span midnight
      return currentTime >= quietStart || currentTime <= quietEnd;
    }
  }

  /**
   * Check if quiet hours should be overridden for this severity
   */
  private shouldOverrideQuietHours(severity: AlertSeverity): boolean {
    return severity === AlertSeverity.CRITICAL;
  }

  // Helper methods for message formatting
  private formatVulnerabilityMessage(data: any): string {
    return `
Critical Vulnerability Detected:
CVE: ${data.cve_id}
CVSS Score: ${data.cvss_score}
Affected System: ${data.hostname}
Description: ${data.description}

Immediate action required. Login to Scan Ninja dashboard for details.
    `.trim();
  }

  private formatScanCompletionMessage(data: any): string {
    const summary = data.vulnerabilitySummary || {};
    return `
Scan "${data.name}" has completed successfully.

Results Summary:
• Critical: ${summary.critical || 0}
• High: ${summary.high || 0}
• Medium: ${summary.medium || 0}
• Low: ${summary.low || 0}

Total Vulnerabilities: ${summary.total || 0}
    `.trim();
  }

  private formatTicketMessage(data: any, updateType: string): string {
    switch (updateType) {
      case 'new_message':
        return `New message in ticket #${data.id}: "${data.subject}"`;
      case 'status_change':
        return `Ticket #${data.id} status changed to: ${data.status}`;
      case 'priority_change':
        return `Ticket #${data.id} priority changed to: ${data.priority}`;
      case 'assignment':
        return `Ticket #${data.id} assigned to: ${data.assignee}`;
      default:
        return `Ticket #${data.id} has been updated`;
    }
  }

  private formatTicketTitle(data: any, updateType: string): string {
    switch (updateType) {
      case 'new_message':
        return `New Message - Ticket #${data.id}`;
      case 'status_change':
        return `Status Update - Ticket #${data.id}`;
      case 'priority_change':
        return `Priority Update - Ticket #${data.id}`;
      case 'assignment':
        return `Assignment Update - Ticket #${data.id}`;
      default:
        return `Ticket Update #${data.id}`;
    }
  }

  private formatComplianceMessage(data: any): string {
    return `
Compliance drift detected:
Control: ${data.control_name}
Framework: ${data.framework}
Resource: ${data.resource_name}
Severity: ${data.severity}

Please review and take corrective action.
    `.trim();
  }

  private formatIncidentMessage(data: any): string {
    return `
${data.description}

Impact: ${data.impact}
Status: ${data.status}
Commander: ${data.commander}
Detected: ${data.detected_at}

${data.bridge_url ? `Join incident bridge: ${data.bridge_url}` : ''}
    `.trim();
  }

  /**
   * Generate template data from alert payload
   */
  private generateTemplateData(alert: AlertPayload): any {
    const origin = isPlatformBrowser(this.platformId) ? window.location.origin : 'https://dashboard.scanninja.app';
    const baseData = {
      logoUrl:
        'https://cdn.prod.website-files.com/67368f6c79297610e225f43b/67378b875c07c4139458e3ba_logo-top.png',
      dashboardUrl: origin + '/dashboard',
      settingsUrl: origin + '/my-account/notifications',
      supportUrl: origin + '/support',
      unsubscribeUrl: origin + '/unsubscribe',
    };

    switch (alert.type) {
      case AlertType.VULNERABILITY_SCAN: {
        if (
          alert.severity === AlertSeverity.CRITICAL &&
          alert.metadata?.vulnerabilityData
        ) {
          const vulnData = alert.metadata.vulnerabilityData;
          const scanTemplateId =
            alert.metadata['scanTemplateId'] ||
            vulnData.scanTemplateId ||
            vulnData.template_id;
          const scanDetailsUrl = scanTemplateId
            ? `/dashboard/scans/view/${scanTemplateId}`
            : baseData.dashboardUrl;

          return {
            ...baseData,
            vulnerabilityName: vulnData.vulnerability_name || alert.title,
            cveId: vulnData.cve_id || 'N/A',
            cvssScore: vulnData.cvss_score || 0,
            vulnerabilityDescription: vulnData.description || alert.message,
            affectedAssetsCount: vulnData.affected_assets?.length || 1,
            riskLevel: vulnData.risk_level || alert.severity,
            exploitability: vulnData.exploitability || 'High',
            affectedAssets: vulnData.affected_assets || [
              {
                name: vulnData.hostname || 'Unknown',
                ipAddress: vulnData.ip_address || 'N/A',
                service: vulnData.service || 'N/A',
                status: 'Vulnerable',
              },
            ],
            vulnDetailsUrl:
              this.makeFullUrl(alert.actions?.[0]?.url) ||
              baseData.dashboardUrl,
            remediationUrl:
              this.makeFullUrl(scanDetailsUrl) ||
              baseData.dashboardUrl + '/remediation',
            vulnerabilityUrl: baseData.dashboardUrl + '/vulnerabilities',
          } as CriticalVulnerabilityTemplateData;
        } else {
          return {
            ...baseData,
            alertTitle: alert.title,
            alertDescription: alert.message,
            severity: this.mapAlertSeverityToTemplateSeverity(alert.severity),
            affectedSystems: this.extractAffectedSystems(alert),
            alertUrl:
              this.makeFullUrl(alert.actions?.[0]?.url) ||
              baseData.dashboardUrl,
          } as SecurityAlertTemplateData;
        }
      }

      case AlertType.SCAN_COMPLETION: {
        const scanData = alert.metadata || {};
        const scanTemplateId =
          scanData['scanTemplateId'] ||
          scanData['templateId'] ||
          scanData['template_id'] ||
          scanData['scanId'];
        const scanDetailsUrl = scanTemplateId
          ? `/dashboard/scans/view/${scanTemplateId}`
          : '/dashboard/scans';

        return {
          ...baseData,
          scanName: scanData['scanName'] || 'Security Scan',
          scanDuration: scanData['duration'] || 'Unknown',
          assetsScanned: scanData['assetsScanned'] || 0,
          completionTime: alert.timestamp.toLocaleString(),
          criticalCount: scanData['critical'] || 0,
          highCount: scanData['high'] || 0,
          mediumCount: scanData['medium'] || 0,
          lowCount: scanData['low'] || 0,
          hasVulnerabilities:
            (scanData['critical'] || 0) +
              (scanData['high'] || 0) +
              (scanData['medium'] || 0) +
              (scanData['low'] || 0) >
            0,
          resultsUrl:
            this.makeFullUrl(scanDetailsUrl) ||
            baseData.dashboardUrl + '/scans',
        } as ScanCompletionTemplateData;
      }

      case AlertType.WEEKLY_REPORT: {
        return {
          ...baseData,
          maintenanceTitle: alert.title,
          maintenanceDescription: alert.message,
          startTime: alert.timestamp.toLocaleString(),
          endTime: 'TBD',
          duration: '1 hour',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          affectedServices: [
            {
              name: 'Reporting Service',
              status: 'Available',
            },
          ],
          statusPageUrl: baseData.dashboardUrl + '/status',
        } as SystemMaintenanceTemplateData;
      }

      case AlertType.TICKET_UPDATE: {
        return {
          ...baseData,
          alertTitle: alert.title,
          alertDescription: alert.message,
          severity: this.mapAlertSeverityToTemplateSeverity(alert.severity),
          affectedSystems: [
            `Ticket #${alert.metadata?.['ticketId'] || 'Unknown'}`,
          ],
          alertUrl:
            this.makeFullUrl(alert.actions?.[0]?.url) ||
            baseData.dashboardUrl + '/dashboard/support',
          ticketId: alert.metadata?.['ticketId'],
          updateType: alert.metadata?.['updateType'],
        } as SecurityAlertTemplateData;
      }

      default: {
        return {
          ...baseData,
          alertTitle: alert.title,
          alertDescription: alert.message,
          severity: this.mapAlertSeverityToTemplateSeverity(alert.severity),
          affectedSystems: this.extractAffectedSystems(alert),
          alertUrl:
            this.makeFullUrl(alert.actions?.[0]?.url) || baseData.dashboardUrl,
        } as SecurityAlertTemplateData;
      }
    }
  }

  /**
   * Extract affected systems from alert metadata
   */
  private extractAffectedSystems(alert: AlertPayload): string[] {
    if (alert.metadata?.['affectedSystems']) {
      return Array.isArray(alert.metadata['affectedSystems'])
        ? alert.metadata['affectedSystems']
        : [alert.metadata['affectedSystems']];
    }

    if (alert.metadata?.['hostname']) {
      return [alert.metadata['hostname']];
    }

    if (alert.metadata?.vulnerabilityData?.['hostname']) {
      return [alert.metadata.vulnerabilityData['hostname']];
    }

    return ['System'];
  }

  /**
   * Map AlertSeverity to template severity format
   */
  private mapAlertSeverityToTemplateSeverity(
    severity: AlertSeverity,
  ): 'critical' | 'high' | 'medium' {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return 'critical';
      case AlertSeverity.HIGH:
        return 'high';
      case AlertSeverity.MEDIUM:
      case AlertSeverity.LOW:
      case AlertSeverity.INFO:
      default:
        return 'medium';
    }
  }

  /**
   * Fallback email body if template generation fails
   */
  private generateFallbackEmailBody(alert: AlertPayload): string {
    const action = alert.actions?.[0];
    return `
<html>
<head>
    <title>ScanNinja Alert</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background: #f5f7fa; }
        .container { max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .header { background: #667eea; color: white; padding: 30px; text-align: center; }
        .content { padding: 30px; }
        .severity { display: inline-block; padding: 6px 12px; border-radius: 4px; font-weight: 600; margin: 10px 0; }
        .severity-critical { background: #dc2626; color: white; }
        .severity-high { background: #f59e0b; color: white; }
        .severity-medium { background: #10b981; color: white; }
        .btn { display: inline-block; padding: 12px 24px; background: #667eea; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 10px 0; }
        .footer { background: #f3f4f6; padding: 20px; text-align: center; color: #6b7280; font-size: 14px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>ScanNinja Security</h1>
            <h2>${alert.title}</h2>
        </div>
        <div class="content">
            <div class="severity severity-${this.mapAlertSeverityToTemplateSeverity(alert.severity)}">${alert.severity}</div>
            <p>${alert.message.replace(/\n/g, '<br>')}</p>
            <p><strong>Time:</strong> ${alert.timestamp.toLocaleString()}</p>
            ${action ? `<p><a href="${action.url}" class="btn">${action.label}</a></p>` : ''}
        </div>
        <div class="footer">
            <p>This notification was sent by ScanNinja™ Alert System</p>
        </div>
    </div>
</body>
</html>
    `.trim();
  }

  private formatSmsMessage(alert: AlertPayload): string {
    const action = alert.actions?.[0];
    return `${alert.severity}: ${alert.title}. ${alert.message.substring(0, 100)}...${action?.url ? ` ${action.url}` : ''}`;
  }

  private determineScanSeverity(scanData: any): AlertSeverity {
    const summary = scanData.vulnerabilitySummary || {};
    if (summary.critical > 0) return AlertSeverity.CRITICAL;
    if (summary.high > 0) return AlertSeverity.HIGH;
    if (summary.medium > 0) return AlertSeverity.MEDIUM;
    return AlertSeverity.INFO;
  }

  private determineTicketSeverity(
    ticketData: any,
    updateType: string,
  ): AlertSeverity {
    if (updateType === 'new_message' && ticketData.priority === 'critical')
      return AlertSeverity.HIGH;
    if (updateType === 'status_change') return AlertSeverity.MEDIUM;
    return AlertSeverity.LOW;
  }

  private mapStringToSeverity(severity: string): AlertSeverity {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return AlertSeverity.CRITICAL;
      case 'high':
        return AlertSeverity.HIGH;
      case 'medium':
        return AlertSeverity.MEDIUM;
      case 'low':
        return AlertSeverity.LOW;
      case 'p1':
        return AlertSeverity.CRITICAL;
      case 'p2':
        return AlertSeverity.HIGH;
      case 'p3':
        return AlertSeverity.MEDIUM;
      case 'p4':
        return AlertSeverity.LOW;
      default:
        return AlertSeverity.INFO;
    }
  }

  private getSeverityEmoji(severity: AlertSeverity): string {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return '🚨';
      case AlertSeverity.HIGH:
        return '⚠️';
      case AlertSeverity.MEDIUM:
        return '⚡';
      case AlertSeverity.LOW:
        return 'ℹ️';
      case AlertSeverity.INFO:
        return '📢';
      default:
        return '🔔';
    }
  }

  private getSignalRTarget(alertType: AlertType): string {
    switch (alertType) {
      case AlertType.VULNERABILITY_SCAN:
        return 'vulnerabilityAlert';
      case AlertType.SCAN_COMPLETION:
        return 'scanComplete';
      case AlertType.TICKET_UPDATE:
        return 'ticketUpdate';
      case AlertType.COMPLIANCE_DRIFT:
        return 'complianceAlert';
      case AlertType.INCIDENT_RESPONSE:
        return 'incidentAlert';
      case AlertType.SYSTEM_STATUS:
        return 'systemAlert';
      default:
        return 'generalAlert';
    }
  }

  private generateAlertId(): string {
    // 6 bytes = 48 bits entropy, hex encoding = 12 chars
    const randomBytes = new Uint8Array(6);
    if (isPlatformBrowser(this.platformId)) {
      window.crypto.getRandomValues(randomBytes);
    } else {
      for (let i = 0; i < randomBytes.length; i++) {
        randomBytes[i] = Math.floor(Math.random() * 256);
      }
    }
    const securePart = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `alert_${Date.now()}_${securePart}`;
  }

  private async loadUserPreferences(
    userId: string,
  ): Promise<UserNotificationPreferences> {
    try {
      // Try to get existing preferences from the notification preferences service
      const existingPreferences =
        this.notificationPreferencesService.getCurrentPreferences();

      if (existingPreferences && existingPreferences.userId === userId) {
        return existingPreferences;
      }

      // If no cached preferences or different user, initialize from storage
      const preferences =
        await this.notificationPreferencesService.initializePreferences(userId);

      if (preferences) {
        return preferences;
      }

      // Fallback to default preferences if loading fails
      this.loggingService.logWarning(
        '⚠️ No user preferences found, using defaults',
        { userId },
      );
      return this.createFallbackPreferences(userId);
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to load user preferences, using defaults',
        { userId, error },
      );
      return this.createFallbackPreferences(userId);
    }
  }

  /**
   * Load user preferences with role-specific defaults
   */
  private async loadUserPreferencesWithRole(
    userId: string,
    userRole: string,
  ): Promise<UserNotificationPreferences> {
    try {
      // Try to get existing preferences from the notification preferences service
      const existingPreferences =
        this.notificationPreferencesService.getCurrentPreferences();

      if (existingPreferences && existingPreferences.userId === userId) {
        return existingPreferences;
      }

      // If no cached preferences or different user, initialize from storage with role
      const preferences =
        await this.notificationPreferencesService.initializePreferences(
          userId,
          userRole,
        );

      if (preferences) {
        return preferences;
      }

      // Fallback to default preferences if loading fails
      this.loggingService.logWarning(
        '⚠️ No user preferences found, using role-based defaults',
        { userId, userRole },
      );
      return this.createFallbackPreferencesWithRole(userId, userRole);
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to load user preferences with role, using defaults',
        { userId, userRole, error },
      );
      return this.createFallbackPreferencesWithRole(userId, userRole);
    }
  }

  /**
   * Create fallback preferences when loading fails
   */
  private createFallbackPreferences(
    userId: string,
  ): UserNotificationPreferences {
    return {
      userId,
      ...this.defaultPreferences,
    } as UserNotificationPreferences;
  }

  /**
   * Create fallback preferences with role-specific defaults
   */
  private createFallbackPreferencesWithRole(
    userId: string,
    userRole: string,
  ): UserNotificationPreferences {
    // Use role-aware defaults from NotificationPreferencesService patterns
    const roleSpecificDefaults =
      userRole === 'admin'
        ? { enableEmailNotifications: true, enableSmsNotifications: true }
        : userRole === 'readonly'
          ? {
              enableEmailNotifications: false,
              enableSmsNotifications: false,
              enableDesktopNotifications: false,
            }
          : { enableEmailNotifications: false, enableSmsNotifications: false };

    return {
      userId,
      ...this.defaultPreferences,
      ...roleSpecificDefaults,
    } as UserNotificationPreferences;
  }

  /**
   * Update user notification preferences
   */
  async updateUserPreferences(
    preferences: Partial<UserNotificationPreferences>,
  ): Promise<boolean> {
    try {
      const current = this.userPreferences$.value;
      if (!current) throw new Error('No current preferences loaded');

      const updated = { ...current, ...preferences };

      // Update local cache immediately
      this.userPreferences$.next(updated);

      // Save to persistent storage via NotificationPreferencesService
      const saveSuccess =
        await this.notificationPreferencesService.updatePreferences(
          preferences,
        );

      if (saveSuccess) {
        return true;
      } else {
        this.loggingService.logWarning(
          '⚠️ User preferences updated locally but failed to save to storage',
          {
            userId: updated.userId,
          },
        );
        return false;
      }
    } catch (error) {
      this.loggingService.logError('❌ Failed to update user preferences:', {
        error,
      });
      return false;
    }
  }

  /**
   * Get current user preferences
   */
  getUserPreferences(): UserNotificationPreferences | null {
    return this.userPreferences$.value;
  }

  /**
   * Process queued alerts (called after quiet hours end)
   */
  async processQueuedAlerts(): Promise<void> {
    if (this.isProcessingQueue || this.alertQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    try {
      const alerts = [...this.alertQueue];
      this.alertQueue = [];

      for (const alert of alerts) {
        await this.sendAlert(alert);
        // Small delay between alerts to avoid overwhelming
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      this.loggingService.logError('❌ Error processing queued alerts:', error);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Map AlertType to NotificationType for notification manager
   */
  private mapAlertTypeToNotificationType(
    alertType: AlertType,
  ): NotificationType {
    switch (alertType) {
      case AlertType.VULNERABILITY_SCAN:
        return NotificationType.CRITICAL_VULNERABILITIES;
      case AlertType.SCAN_COMPLETION:
        return NotificationType.SCAN_COMPLETION;
      case AlertType.COMPLIANCE_DRIFT:
        return NotificationType.COMPLIANCE_ALERTS;
      case AlertType.INCIDENT_RESPONSE:
        return NotificationType.SECURITY_ALERTS;
      case AlertType.TICKET_UPDATE:
        return NotificationType.REMEDIATION_UPDATES;
      case AlertType.SYSTEM_STATUS:
        return NotificationType.SYSTEM_MAINTENANCE;
      case AlertType.WEEKLY_REPORT:
        return NotificationType.PLATFORM_UPDATES;
      default:
        return NotificationType.SECURITY_ALERTS; // Default to security alerts for unknown types
    }
  }

  /**
   * Convert relative URLs to full URLs for email templates
   */
  private makeFullUrl(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }

    // If URL is already absolute (contains protocol), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    const origin = isPlatformBrowser(this.platformId) ? window.location.origin : 'https://dashboard.scanninja.app';

    // If URL is relative, prepend the origin
    if (url.startsWith('/')) {
      return origin + url;
    }

    // If URL doesn't start with '/', add it
    return origin + '/' + url;
  }
}
